import React, { useEffect, useState } from 'react';
import { useField } from 'formik';
import { FormElement, Label } from '@dashboard/components/Form';
import { Select } from '@dashboard/components/Formik';
import FormError from '@dashboard/components/Formik/components/FormError';
import { elementFieldTypeEnums } from '@dashboard/services/pageFieldTypes';

// Fields that can be used as triggers for conditional logic
export const SUPPORTED_TRIGGER_FIELDS = [
  elementFieldTypeEnums.SELECT,
  elementFieldTypeEnums.MULTI_SELECT,
  elementFieldTypeEnums.MULTIPLE_CHOICE,
  elementFieldTypeEnums.CHECKBOX,
];

function getShouldSelectOption(comparison) {
  return comparison === 'IS' || comparison === 'IS_NOT';
}

// A disabled trigger can never be answered, so it is not offered for new
// rules. An existing rule keeps its trigger listed so the seller can see it.
export function getAvailableTriggerFields(fields, currentFieldId, selectedFieldId) {
  return fields
    .filter(f => f._id !== currentFieldId && SUPPORTED_TRIGGER_FIELDS.includes(f.element))
    .filter(f => !f.disabled || f._id === selectedFieldId)
    .map(f => ({ ...f, id: f._id || f.key }));
}

export default function ShowHideLogicField({ fieldKey, field, fields }) {
  const [logicField, , helpers] = useField('showHideLogic');
  const { setValue } = helpers;

  const currentFieldId = field._id || field.key;

  const [selectedFieldId, setSelectedFieldId] = useState(
    logicField.value?.element?.elementId || null,
  );
  const [selectedValue, setSelectedValue] = useState(logicField.value?.value || null);

  const availableFields = getAvailableTriggerFields(fields, currentFieldId, selectedFieldId);

  const selectedField = availableFields.find(f => f._id === selectedFieldId);
  const isSelectedFieldDisabled = selectedField?.disabled === true;
  const isCheckboxField = selectedField?.element === elementFieldTypeEnums.CHECKBOX;
  const isSelectOrMultiField =
    selectedField?.element === elementFieldTypeEnums.SELECT ||
    selectedField?.element === elementFieldTypeEnums.MULTIPLE_CHOICE ||
    selectedField?.element === elementFieldTypeEnums.MULTI_SELECT;

  useEffect(() => {
    if (selectedFieldId) {
      const targetField = availableFields.find(f => f._id === selectedFieldId);

      if (!targetField) {
        return;
      }

      setValue({
        ...logicField.value,
        element: {
          elementId: targetField._id,
          elementType: 'field',
          elementTitle: targetField.label,
        },
      });
    }
  }, [selectedFieldId]);

  useEffect(() => {
    if (selectedValue !== null) {
      setValue({
        ...logicField.value,
        value: selectedValue,
      });
    }
  }, [selectedValue]);

  return (
    <div className="ml-[26px] !space-y-2 !mb-2 !mt-2">
      <FormElement>
        <div className="flex items-center space-x-2">
          <Label isInline htmlFor={`${fieldKey}.showHideLogic.element.elementId`} className="w-16">
            Field
          </Label>

          <Select
            id={`${fieldKey}.showHideLogic.element.elementId`}
            name="showHideLogic.element.elementId"
            onChange={e => {
              setSelectedFieldId(e.target.value || null);
            }}
            value={selectedFieldId || ''}
            emptyMessage="No field can show or hide this one yet."
          >
            <option value="" default>
              Select field...
            </option>
            {availableFields.map(f => (
              <option key={f._id} value={f._id}>
                {f.label}
              </option>
            ))}
          </Select>
        </div>
        <FormError name="showHideLogic.element.elementId" />
        <FormError name="showHideLogic.element.elementType" />
        <FormError name="showHideLogic.element.elementTitle" />
        {isSelectedFieldDisabled && (
          <p className="text-sm text-gray-600 mt-1">
            &quot;{selectedField.label}&quot; is disabled, so this field is shown to everyone until
            it is enabled again.
          </p>
        )}
      </FormElement>
      <>
        <FormElement>
          <div className="flex items-center gap-x-2">
            <div className="w-[54px]" />

            <Select
              id={`${fieldKey}.showHideLogic.comparison`}
              name="showHideLogic.comparison"
              className="w-32"
            >
              <option value="" default>
                Select condition...
              </option>
              <option value="IS">is</option>
              <option value="IS_NOT">is not</option>
              {!isCheckboxField && <option value="IS_EMPTY">is empty</option>}
              {!isCheckboxField && <option value="IS_NOT_EMPTY">is not empty</option>}
            </Select>
          </div>

          <FormError name="showHideLogic.comparison" />
        </FormElement>

        {selectedField && getShouldSelectOption(logicField.value?.comparison) && (
          <FormElement>
            <div className="flex items-center gap-x-2">
              {isCheckboxField ? (
                <>
                  <Label isInline htmlFor={`${fieldKey}.showHideLogic.value`} className="w-[54px]">
                    Value
                  </Label>

                  <Select
                    id={`${fieldKey}.showHideLogic.value`}
                    name="showHideLogic.value"
                    className="w-32"
                    onChange={e => {
                      setSelectedValue(e.target.value || null);
                    }}
                    value={selectedValue || ''}
                  >
                    <option value="" default>
                      Select value...
                    </option>
                    <option value="TRUE">Checked</option>
                    <option value="FALSE">Unchecked</option>
                  </Select>
                </>
              ) : isSelectOrMultiField ? (
                <>
                  <Label isInline htmlFor={`${fieldKey}.showHideLogic.value`} className="w-[54px]">
                    Value
                  </Label>

                  <Select
                    id={`${fieldKey}.showHideLogic.value`}
                    name="showHideLogic.value"
                    className="w-32"
                    onChange={e => {
                      setSelectedValue(e.target.value || null);
                    }}
                    value={selectedValue || ''}
                  >
                    <option value="" default>
                      Select option...
                    </option>
                    {selectedField.options?.map(option => (
                      <option key={option.key} value={option._id}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </>
              ) : null}
            </div>

            <FormError name="showHideLogic.value" />
          </FormElement>
        )}
      </>
    </div>
  );
}
